import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteLogo } from '../contexts/SiteLogoContext';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n, useTranslate } from '../contexts/I18nContext';

export const Footer = () => {
  const { logoUrl, darkLogoUrl, siteName, getFullLogoUrl, getFullDarkLogoUrl } = useSiteLogo();
  const { isDark } = useTheme();
  const { t } = useI18n();
  const { translateText } = useTranslate();

  const displayLogoUrl = isDark && darkLogoUrl ? getFullDarkLogoUrl() : getFullLogoUrl();
  const linkBase = `text-sm hover:text-[#00CC66] transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const headingBase = `text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-[#064E3B]'}`;

  return (
    <footer className={`py-12 border-t ${isDark ? 'night-footer' : 'bg-white border-gray-100'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start gap-3">
            <div className="flex items-center gap-2 min-h-[32px]">
              {displayLogoUrl ? (
                <img
                  src={displayLogoUrl}
                  alt={`${siteName} - Directorio de panoramas y eventos en Chile`}
                  className="h-8 max-w-[150px] object-contain"
                />
              ) : (
                <span className={`font-bold ${isDark ? 'text-white' : 'text-[#064E3B]'}`}>{siteName}</span>
              )}
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'} max-w-xs text-center md:text-left`}>
              {translateText('Tu directorio de panoramas, conciertos, ferias, festivales y actividades locales en Chile.')}
            </p>
          </div>

          {/* Quick links */}
          <nav aria-label="Enlaces rapidos" className="flex flex-col items-center md:items-start gap-2">
            <h3 className={headingBase}>{translateText('Descubre')}</h3>
            <span className={`${linkBase} cursor-default hover:!text-inherit`}>{translateText('Explorar panoramas')}</span>
            <span className={`${linkBase} cursor-default hover:!text-inherit`}>{translateText('Mapa de eventos')}</span>
            <span className={`${linkBase} cursor-default hover:!text-inherit`}>{translateText('Conciertos')}</span>
            <span className={`${linkBase} cursor-default hover:!text-inherit`}>{translateText('Ferias y mercados')}</span>
          </nav>

          {/* SEO content */}
          <nav aria-label="Categorias" className="flex flex-col items-center md:items-start gap-2">
            <h3 className={headingBase}>{translateText('Actividades')}</h3>
            <span className={`${linkBase} cursor-default hover:!text-inherit`}>{translateText('Escapadas y paseos')}</span>
            <span className={`${linkBase} cursor-default hover:!text-inherit`}>{translateText('Festivales')}</span>
            <span className={`${linkBase} cursor-default hover:!text-inherit`}>{translateText('Talleres y cursos')}</span>
            <span className={`${linkBase} cursor-default hover:!text-inherit`}>{translateText('Actividades familiares')}</span>
          </nav>

          {/* Legal */}
          <nav aria-label="Informacion legal" className="flex flex-col items-center md:items-start gap-2">
            <h3 className={headingBase}>{translateText('Legal')}</h3>
            <Link to="/terminos" className={linkBase} data-testid="footer-terms">{translateText('Términos y Condiciones')}</Link>
            <Link to="/privacidad" className={linkBase} data-testid="footer-privacy">{translateText('Política de Privacidad')}</Link>
            <Link to="/cookies" className={linkBase} data-testid="footer-cookies">{translateText('Política de Cookies')}</Link>
            <Link to="/aviso-legal" className={linkBase} data-testid="footer-legal-notice">{translateText('Aviso Legal')}</Link>
            <Link to="/contacto" className={linkBase} data-testid="footer-contact">{translateText('Contacto')}</Link>
          </nav>
        </div>

        <div className={`border-t pt-6 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          <div className="flex flex-col md:flex-row items-center md:justify-between gap-3 text-center md:text-left">
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              &copy; 2026 {siteName}. {t('allRightsReserved')}.
            </p>
            <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'} max-w-2xl`}>
              {translateText('Plataforma operada en Chile. Cumplimos con la Ley N° 19.628 sobre Protección de la Vida Privada y sus modificaciones por Ley N° 21.719.')}
            </p>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-1">
            <Link to="/terminos" className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>{translateText('Términos')}</Link>
            <span className={`text-xs ${isDark ? 'text-gray-700' : 'text-gray-300'}`}>·</span>
            <Link to="/privacidad" className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>{translateText('Privacidad')}</Link>
            <span className={`text-xs ${isDark ? 'text-gray-700' : 'text-gray-300'}`}>·</span>
            <Link to="/cookies" className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>{translateText('Cookies')}</Link>
            <span className={`text-xs ${isDark ? 'text-gray-700' : 'text-gray-300'}`}>·</span>
            <Link to="/aviso-legal" className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>{translateText('Aviso Legal')}</Link>
            <span className={`text-xs ${isDark ? 'text-gray-700' : 'text-gray-300'}`}>·</span>
            <Link to="/contacto" className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`} data-testid="footer-contact-mini">{translateText('Contacto')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
